import { FC } from "react";

import { twMerge } from "tailwind-merge";

import { groupChatsByBucket } from "./chatHistoryBuckets";
import ChatListLoadMoreFooter, {
  ChatListSkeletonRow
} from "./ChatListLoadMoreFooter";
import {
  AGENTIC_AI_CHAT_HISTORY_EMPTY,
  AGENTIC_AI_CHAT_HISTORY_ERROR,
  AGENTIC_AI_CHAT_HISTORY_HEADING,
  AGENTIC_AI_CHAT_HISTORY_RETRY,
  type ChatBucket,
  type ChatHistoryEntry,
  type ChatListLoadMore
} from "./constants";

const SKELETON_TITLE_WIDTHS = ["w-3/4", "w-2/3", "w-5/6"];

type Props = {
  chats: ChatHistoryEntry[];
  activeChatId: string | null;
  onSelect: (chatId: string) => void;
  isLoading: boolean;
  /** The first load failed and there are no rows to keep. */
  isError: boolean;
  onRetry: () => void;
  loadMore?: ChatListLoadMore;
};

type ChatBucketSectionProps = Pick<Props, "activeChatId" | "onSelect"> & {
  bucket: ChatBucket;
  label: string;
  chats: ChatHistoryEntry[];
};

const ChatBucketSection: FC<ChatBucketSectionProps> = ({
  bucket,
  label,
  chats,
  activeChatId,
  onSelect
}) => {
  const headingId = `agentic-ai-chat-bucket-${bucket}`;

  return (
    <div>
      <h3
        id={headingId}
        className="sticky top-0 z-10 bg-white px-2 pb-1 pt-2 text-xs font-semibold text-txtBody"
      >
        {label}
      </h3>
      <ul aria-labelledby={headingId} className="flex flex-col gap-1">
        {chats.map(({ id, title }) => {
          const isActive = id === activeChatId;

          return (
            <li key={id}>
              <button
                type="button"
                onClick={() => onSelect(id)}
                aria-current={isActive ? "page" : undefined}
                className={twMerge(
                  "flex w-full flex-col rounded-md px-2 py-2 text-left transition-colors hover:bg-teal-50",
                  isActive && "bg-teal-50"
                )}
              >
                <span className="truncate text-sm text-txtHeading font-semibold">
                  {title}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const ChatHistoryList: FC<Props> = ({
  chats,
  activeChatId,
  onSelect,
  isLoading,
  isError,
  onRetry,
  loadMore
}) => {
  const renderBody = () => {
    if (isLoading) {
      return (
        <ul className="flex flex-col gap-1">
          {SKELETON_TITLE_WIDTHS.map((titleWidth) => (
            <ChatListSkeletonRow key={titleWidth} titleWidth={titleWidth} />
          ))}
        </ul>
      );
    }

    if (isError) {
      return (
        <div className="flex flex-col items-start gap-1 px-2 text-xs text-txtBody">
          <p>{AGENTIC_AI_CHAT_HISTORY_ERROR}</p>
          <button
            type="button"
            onClick={onRetry}
            className="font-semibold text-teal-500 hover:underline"
          >
            {AGENTIC_AI_CHAT_HISTORY_RETRY}
          </button>
        </div>
      );
    }

    if (chats.length === 0) {
      return (
        <p className="px-2 text-xs leading-relaxed text-txtBody">
          {AGENTIC_AI_CHAT_HISTORY_EMPTY}
        </p>
      );
    }

    return (
      <>
        {groupChatsByBucket(chats).map(
          ({ bucket, label, chats: bucketChats }) => (
            <ChatBucketSection
              key={bucket}
              bucket={bucket}
              label={label}
              chats={bucketChats}
              activeChatId={activeChatId}
              onSelect={onSelect}
            />
          )
        )}
        {loadMore && (
          <ul>
            <ChatListLoadMoreFooter loadMore={loadMore} />
          </ul>
        )}
      </>
    );
  };

  return (
    <nav
      aria-labelledby="agentic-ai-chat-history"
      aria-busy={isLoading}
      className="flex min-h-0 flex-1 flex-col overflow-y-auto px-2 pb-4"
    >
      <div
        id="agentic-ai-chat-history"
        className="py-3 px-2 text-base font-bold text-txtHeading"
      >
        {AGENTIC_AI_CHAT_HISTORY_HEADING}
      </div>

      {renderBody()}
    </nav>
  );
};

export default ChatHistoryList;
